import { Fragment, memo, useMemo, type ReactNode } from 'react'
import type { ChallengeService } from '@duck/api-definitions'
import { cn } from '@duck/ui'
import { useTar, type TranslationKey } from '@/modules/i18n'
import { useFormatDuration } from './useFormatDuration'
import { SEVERITY_BADGE_CLASS, SEVERITY_COLOR } from './severity'
import { isInsert, typingMs, type Session, type SessionSplit } from './sessions'
import { dayOf, hhmm, timeOf } from './format'

// Display-only noise filters: a stray Enter before a paste, or a sub-second tab flick, still count in the
// totals but are not worth a line.
const TYPING_NOISE_KEYS = 5
const AWAY_NOISE_MS = 2_000

/** Anchor id used by the summary panel to jump to the first event of a rule. */
export const ruleAnchorId = (rule: string) => `activity-rule-${rule}`

const Step = ({ time, dot, flag, id, children }: { time?: string; dot?: string; flag?: ChallengeService.ActivitySeverityEnum; id?: string; children: ReactNode }) => (
  <div id={id} className="relative grid grid-cols-[96px_minmax(0,1fr)] gap-3 py-1.5 text-sm">
    <span
      className={cn('absolute top-[13px] -start-[24px] rounded-full', flag !== undefined ? 'h-2.5 w-2.5 top-[11px] -start-[26px]' : 'h-1.5 w-1.5')}
      style={flag !== undefined ? { background: SEVERITY_COLOR[flag], boxShadow: `0 0 0 4px color-mix(in srgb, ${SEVERITY_COLOR[flag]} 25%, transparent)` } : { background: dot ?? 'hsl(var(--color-muted-foreground))' }}
    />
    <time className="whitespace-nowrap pt-0.5 text-xs text-muted-foreground tabular-nums" dir="ltr">{time ?? ''}</time>
    <div className={cn(flag !== undefined && 'rounded-md border bg-card px-3 py-2')} style={flag !== undefined ? { borderColor: `color-mix(in srgb, ${SEVERITY_COLOR[flag]} 40%, hsl(var(--color-border)))` } : undefined}>{children}</div>
  </div>
)

const Preview = ({ text, className }: { text: string; className?: string }) => (
  <pre className={cn('mt-1.5 max-w-[60ch] overflow-hidden whitespace-pre-wrap rounded bg-foreground/10 px-2.5 py-2 font-mono text-xs text-foreground', className)} dir="ltr">{text}</pre>
)

const SessionLedger = memo(({ session, firstByRule }: { session: Session; firstByRule: Map<string, number> }) => {
  const { t } = useTar()
  const formatDuration = useFormatDuration()
  const steps: ReactNode[] = []
  session.events.forEach((e, i) => {
    if (e.type === 'save' || e.type === 'focus') return
    const flag = e.label === 'info' ? undefined : e.label
    const id = e.rule !== undefined && firstByRule.get(e.rule) === e.t ? ruleAnchorId(e.rule) : undefined
    const surface = e.surface !== undefined ? t(`adminActivityReport.surfaces.${e.surface}`) : ''
    let body: ReactNode = null
    let dot: string | undefined
    if (e.type === 'active') {
      if ((e.len ?? 0) < TYPING_NOISE_KEYS) return
      const ms = typingMs(e, session.events, i)
      body = (
        <span className="text-muted-foreground">
          <i className="me-2 inline-block h-2 rounded-sm bg-primary align-middle opacity-85" style={{ width: Math.max(8, Math.min(160, ms / 1500)) }} />
          {t(e.surface !== undefined ? 'adminActivityReport.ledger.typed' : 'adminActivityReport.ledger.typedNoSurface', { duration: formatDuration(ms), n: e.len ?? 0, surface })}
        </span>
      )
    } else if (isInsert(e)) {
      const prev = session.events[i - 1]
      const afterReturn = prev !== undefined && prev.type === 'focus' && prev.awayMs !== undefined
      const pause = e.pauseMs !== undefined && e.pauseMs > 0
        ? afterReturn
          ? t('adminActivityReport.ledger.afterReturn', { duration: formatDuration(e.pauseMs), away: formatDuration(prev.awayMs ?? 0) })
          : t('adminActivityReport.ledger.afterPrevious', { duration: formatDuration(e.pauseMs) })
        : ''
      body = (
        <>
          <b className="font-medium">{t(`adminActivityReport.ledger.${e.type as 'paste' | 'drop' | 'inject'}`, { n: e.len ?? 0, surface })}</b>
          {e.replacedAll === true ? `, ${t('adminActivityReport.ledger.replacedAll')}` : ''}
          {e.internal === true ? ` (${t('adminActivityReport.ledger.internal')})` : e.internal === false ? ` ${t('adminActivityReport.ledger.external')}` : ''}
          {pause !== '' ? `, ${pause}` : ''}.
          {flag !== undefined ? <span className={cn('ms-2 whitespace-nowrap rounded px-2 py-0.5 text-xs', SEVERITY_BADGE_CLASS[flag])}>{t(`adminActivityReport.severity.${flag}`)}</span> : null}
          {/* unknown rules render their raw key by design */}
          {e.rule !== undefined ? <span className="ms-2 text-xs text-muted-foreground">{t(`adminActivityReport.rules.${e.rule}` as TranslationKey)}</span> : null}
          {e.preview !== undefined ? <Preview text={e.preview} /> : null}
        </>
      )
    } else if (e.type === 'run') {
      const failed = e.error !== undefined && e.error !== null && e.error !== ''
      dot = failed ? SEVERITY_COLOR.highly_suspicious : 'hsl(var(--color-primary))'
      body = (
        <>
          <b className="font-medium">{t(failed ? 'adminActivityReport.ledger.ranFailed' : 'adminActivityReport.ledger.ran', { surface })}</b>
          {e.rule !== undefined ? <span className="ms-2 text-xs text-muted-foreground">{t(`adminActivityReport.rules.${e.rule}` as TranslationKey)}</span> : null}
          {failed ? <span className="ms-2 font-mono text-xs text-error" dir="ltr">{e.error}</span> : e.preview !== undefined ? <span className="ms-2 font-mono text-xs text-muted-foreground" dir="ltr">{e.preview}</span> : null}
        </>
      )
    } else if (e.type === 'blur') {
      const back = session.events.slice(i + 1).find((f) => f.type === 'focus')
      if (back === undefined || back.t - e.t < AWAY_NOISE_MS) return
      body = t('adminActivityReport.ledger.leftFor', { duration: formatDuration(back.t - e.t) })
    } else if (e.type === 'multi_tab') {
      body = t('adminActivityReport.ledger.secondTab')
    } else if (e.type === 'submit') {
      dot = 'hsl(var(--color-card-foreground))'
      body = <b className="font-medium">{t('adminActivityReport.ledger.submitted')}</b>
    } else {
      return
    }
    steps.push(<Step key={`${e.t}-${i}`} time={timeOf(e.t)} dot={dot} flag={flag} id={id}>{body}</Step>)
  })
  const saves = session.events.filter((e) => e.type === 'save').length
  if (saves > 0) steps.push(<Step key="saves"><span className="text-muted-foreground">{t('adminActivityReport.ledger.savedTimes', { n: saves })}</span></Step>)

  return (
    <>
      <h2 className="relative mb-1.5 font-fraunces text-xl">
        <span className="absolute -start-[27px] top-2 h-3 w-3 rounded-full border-2 border-muted-foreground bg-background" />
        {dayOf(session.start)}
        <small className="ms-2.5 font-sans text-[13px] text-muted-foreground" dir="ltr">{hhmm(session.start)}–{hhmm(session.end)} · {t('adminActivityReport.ledger.activeMinutes', { n: session.activeMinutes })}</small>
      </h2>
      {steps}
    </>
  )
})

export const ActivityLedger = ({ split: { sessions, breaks } }: { split: SessionSplit }) => {
  const { t } = useTar()
  const formatDuration = useFormatDuration()
  // First event per rule, so the summary's jump links land on the earliest occurrence.
  const firstByRule = useMemo(() => {
    const first = new Map<string, number>()
    for (const s of sessions) for (const e of s.events) if (e.rule !== undefined && !first.has(e.rule)) first.set(e.rule, e.t)
    return first
  }, [sessions])
  return (
    <div className="relative ps-7 before:absolute before:bottom-2 before:top-2 before:start-[7px] before:w-0.5 before:bg-border">
      {sessions.map((s, i) => (
        <Fragment key={s.start}>
          <SessionLedger session={s} firstByRule={firstByRule} />
          {breaks[i] !== undefined ? (
            <div className="relative my-2.5 mb-4 py-2.5 text-[13px] text-muted-foreground before:absolute before:-start-[22px] before:top-0 before:bottom-0 before:w-0.5 before:border-s-2 before:border-dashed before:border-muted before:bg-background">
              {t('adminActivityReport.ledger.awayFor', { duration: formatDuration(breaks[i]) })}
            </div>
          ) : null}
        </Fragment>
      ))}
    </div>
  )
}
